import { useEffect, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import { getDashboardUsers } from '../../../../../../../utils/api_service/dashboard';
import {
  addCandidatesToOffer,
  getCandidateToOffer,
  saveCandidateToOffer,
  updateJobApplicantStatus,
} from '../../../../../../../utils/api_service/JobOpenings';
import { findUser } from '../../../../../../../utils/api_service/user_api';
import { IOfferDetails } from '../Interfaces';
import { IOffer } from '../Offer';

// Interface for user data
interface UserData {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture?: string;
  // Additional fields that might be in the API response
  id?: string;
  profile_image?: string; // Some APIs might use this instead of profile_picture
}

export const useOffer = ({ jobOpeningId }: IOffer) => {
  const [candidates, setCandidates] = useState<IOfferDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] =
    useState<string | null>(null);
  const [users, setUsers] = useState<Record<string, UserData>>({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Fetch candidates from the offer API
  useEffect(() => {
    const fetchOfferCandidates = async () => {
      if (!jobOpeningId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await getCandidateToOffer(
          jobOpeningId,
          'Offer',
          page,
          limit,
        );

        if (response.ok) {
          const data = await response.json();
          // Extract candidates and total count from the response
          if (data && typeof data === 'object') {
            setCandidates(data.applicants || []);
            setTotalCandidates(data.total || 0);
          }
        } else {
          setError(`Failed to fetch candidates (${response.status})`);

          if (response.status === 404) {
            // If no offer exists yet, initialize with empty array
            setCandidates([]);
            setTotalCandidates(0);
          }
        }
      } catch (error) {
        setError('Failed to load candidates from offer');
        toast.error('Failed to load candidates from offer', {
          position: 'bottom-center',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfferCandidates();
  }, [jobOpeningId, page, limit]);

  // Function to add candidates to offer stage
  const addCandidatesFromPreviousRound = async (
    candidateIds: string[] = [],
  ) => {
    if (!jobOpeningId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use the correct function from JobOpenings.ts
      const response = await saveCandidateToOffer(jobOpeningId, candidateIds);

      if (response.ok) {
        const data = await response.json();
        toast.success('Candidates added to offer stage successfully', {
          position: 'bottom-center',
        });
        return data;
      }
    } catch (error) {
      setError('Failed to add candidates to offer');
      toast.error('Failed to add candidates to offer stage', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get user initials from user object
  const getUserInitials = (user: any): string => {
    if (!user) return '??';

    try {
      // Handle both old and new API formats
      const firstName = user.firstName || user.first_name || '';
      const lastName = user.lastName || user.last_name || '';

      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } catch (error) {
      return '??';
    }
  };

  // Get status color based on offer status and score
  const getStatusColor = (candidate: IOfferDetails): string => {
    // Colors based on status
    switch (candidate.applicant_status) {
      case 'Offer-Accepted':
        return 'bg-emerald-500'; // Bright green for accepted
      case 'Offer-Extended':
        return 'bg-blue-500'; // Blue for extended
      case 'Offer-Declined':
        return 'bg-orange-500'; // Orange for declined (by candidate)
      case 'Offer-Withheld':
        return 'bg-amber-500'; // Amber for withheld
      case 'Offer':
        return 'bg-indigo-500'; // Default for initial offer status
      case 'Interview': {
        // Use score-based colors for candidates without an offer decision
        const aiScore = candidate.interviewApplicant.aiOverallScore?.score || 0;
        if (aiScore >= 70) return 'bg-teal-500';
        if (aiScore >= 50) return 'bg-sky-500';
        return 'bg-gray-500';
      }
      default:
        return 'bg-gray-500'; // Default gray for unknown status
    }
  };

  // Update candidate status (Accept, Reject, Withhold)
  const updateCandidateOfferStatus = async (
    candidateId: string,
    status: string,
  ) => {
    if (!candidateId || !status || isUpdatingStatus || !jobOpeningId) return;

    setIsUpdatingStatus(true);
    try {
      const requestObject = {
        jobApplicantIds: [candidateId],
        applicantStatus: status,
      };

      const response = await updateJobApplicantStatus(
        jobOpeningId,
        requestObject,
      );

      if (response.ok) {
        // Update local state to reflect the change
        setCandidates(
          candidates.map((c) =>
            c.jobApplicantId === candidateId ? { ...c, applicant_status: status } : c,
          ),
        );

        toast.success(`Candidate status updated to ${status}`, {
          position: 'bottom-center',
        });
      } else {
        toast.error(`Failed to update status to ${status}`, {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Failed to update candidate status', {
        position: 'bottom-center',
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const selectedCandidate = candidates.find(c => c.jobApplicantId === selectedCandidateId) || null;

  return {
    candidates,
    isLoading,
    error,
    selectedCandidate, // Reference to the found candidate
    selectedCandidateId,
    getStatusColor,
    getUserInitials,
    totalCandidates,
    setSelectedCandidateId,
    limit,
    page,
    setPage,
    setCandidates,
    updateCandidateOfferStatus,
    loadingUsers,
    isUpdatingStatus,
    addCandidatesFromPreviousRound, // Export the new function
  };
};
